import { useEffect, useState } from "react";
import { ethers } from "ethers";
import {
  provider, ESCROW_ABI, ERC20_ABI, DEAL_STATES, fmtUnits, shortHex, ago, type Manifest,
} from "../chain";

interface Row {
  number: number; timestamp: number; txCount: number; hash: string; gasUsed: bigint;
}

interface Found {
  kind: "tx" | "address" | "block" | "deal" | "none";
  body: React.ReactNode;
}

export default function Explorer({ m }: { m: Manifest }) {
  const [blocks, setBlocks] = useState<Row[]>([]);
  const [q, setQ] = useState("");
  const [found, setFound] = useState<Found>();
  const [searching, setSearching] = useState(false);
  const [err, setErr] = useState<string>();

  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const p = provider();
        const head = await p.getBlockNumber();
        // Only the tail. A full index is a real indexer's job, and pretending
        // to be one by walking every block would be slow and still wrong.
        const want = [];
        for (let n = head; n >= Math.max(0, head - 14); n--) want.push(n);
        const got = await Promise.all(want.map((n) => p.getBlock(n)));
        if (!live) return;
        setBlocks(got.filter(Boolean).map((b) => ({
          number: b!.number, timestamp: b!.timestamp, txCount: b!.transactions.length,
          hash: b!.hash ?? "", gasUsed: b!.gasUsed,
        })));
        setErr(undefined);
      } catch (e) { if (live) setErr((e as Error).message); }
    };
    load();
    const h = setInterval(load, 6000);
    return () => { live = false; clearInterval(h); };
  }, [m]);

  const search = async () => {
    const term = q.trim();
    if (!term) return;
    setSearching(true); setFound(undefined);
    try {
      const p = provider();

      if (ethers.isAddress(term)) {
        const [bal, code] = await Promise.all([p.getBalance(term), p.getCode(term)]);
        const token = new ethers.Contract(m.contracts.token, ERC20_ABI, p);
        const tok = (await token.balanceOf(term)) as bigint;
        const known = Object.entries(m.contracts)
          .find(([, a]) => a.toLowerCase() === term.toLowerCase())?.[0];
        setFound({ kind: "address", body: (
          <table><tbody>
            <tr><th>Address</th><td className="mono">{term}</td></tr>
            {known && <tr><th>Known as</th><td><span className="pill good">{known}</span></td></tr>}
            <tr><th>Type</th><td>{code === "0x" ? "externally owned account" : `contract (${(code.length - 2) / 2} bytes)`}</td></tr>
            <tr><th>Native</th><td>{ethers.formatEther(bal)}</td></tr>
            <tr><th>{m.token.symbol}</th><td>{fmtUnits(tok, m.token.decimals)}</td></tr>
          </tbody></table>
        )});
        return;
      }

      if (/^0x[0-9a-fA-F]{64}$/.test(term)) {
        // Could be a transaction hash, a block hash, or a deal id. Try each,
        // because a user pasting 32 bytes does not know which we call it.
        const tx = await p.getTransaction(term).catch(() => null);
        if (tx) {
          const rc = await p.getTransactionReceipt(term);
          setFound({ kind: "tx", body: (
            <table><tbody>
              <tr><th>Transaction</th><td className="mono">{term}</td></tr>
              <tr><th>Status</th><td>{rc?.status === 1
                ? <span className="pill good">success</span>
                : <span className="pill bad">reverted</span>}</td></tr>
              <tr><th>Block</th><td>{tx.blockNumber}</td></tr>
              <tr><th>From</th><td className="mono">{tx.from}</td></tr>
              <tr><th>To</th><td className="mono">{tx.to ?? "contract creation"}</td></tr>
              <tr><th>Value</th><td>{ethers.formatEther(tx.value)}</td></tr>
              <tr><th>Gas used</th><td>{rc?.gasUsed?.toString() ?? "—"} <span className="dim">at price 0</span></td></tr>
              <tr><th>Logs</th><td>{rc?.logs.length ?? 0}</td></tr>
            </tbody></table>
          )});
          return;
        }

        const escrow = new ethers.Contract(m.contracts.escrow, ESCROW_ABI, p);
        const deal = await escrow.getDeal(term).catch(() => null);
        if (deal && Number(deal.state) !== 0) {
          const commitments = await escrow.shareCommitments(term) as string[];
          setFound({ kind: "deal", body: (
            <>
              <table><tbody>
                <tr><th>Deal</th><td className="mono">{term}</td></tr>
                <tr><th>State</th><td><span className="pill">{DEAL_STATES[Number(deal.state)]}</span></td></tr>
                <tr><th>Buyer</th><td className="mono">{deal.buyer}</td></tr>
                <tr><th>Seller</th><td className="mono">{deal.seller}</td></tr>
                <tr><th>Total</th><td>{fmtUnits(deal.total, m.token.decimals)} {m.token.symbol}</td></tr>
                <tr><th>Released</th><td>{fmtUnits(deal.released, m.token.decimals)} {m.token.symbol}</td></tr>
                <tr><th>Content hash</th><td className="mono">{deal.contentHash}</td></tr>
                <tr><th>Share commitments</th><td className="mono" style={{ fontSize: 12 }}>
                  {commitments.map((c, i) => <div key={i}>[{i}] {c}</div>)}
                </td></tr>
              </tbody></table>
              <div className="note" style={{ marginTop: 12 }}>
                The deal's content is on chain as ciphertext and stays there. What the
                commitments prove is that a holder still has the share they were given —
                they are hashes of uniform random bytes and reveal nothing about the deal.
              </div>
            </>
          )});
          return;
        }

        const blk = await p.getBlock(term).catch(() => null);
        if (blk) { setFound({ kind: "block", body: <BlockCard b={blk} /> }); return; }
      }

      if (/^\d+$/.test(term)) {
        const blk = await p.getBlock(Number(term));
        if (blk) { setFound({ kind: "block", body: <BlockCard b={blk} /> }); return; }
      }

      setFound({ kind: "none", body: (
        <p className="dim" style={{ margin: 0 }}>
          Nothing matched. Try a block number, a 32-byte hash (transaction, block or deal id),
          or an address.
        </p>
      )});
    } catch (e) {
      setFound({ kind: "none", body: <p className="err" style={{ margin: 0 }}>{(e as Error).message}</p> });
    } finally {
      setSearching(false);
    }
  };

  return (
    <>
      <h1>Explorer</h1>
      <p className="sub">
        Blocks, transactions, addresses and deals. A deal id resolves to its on-chain
        record — state, parties, amounts and share commitments — never to its contents.
      </p>

      <div className="card">
        <div className="row">
          <input
            className="mono" style={{ flex: "1 1 420px" }}
            placeholder="block number, 0x… hash, deal id, or address"
            value={q} spellCheck={false}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
          />
          <button className="go" onClick={search} disabled={!q.trim() || searching}>
            {searching ? <><span className="spin" /> looking</> : "Search"}
          </button>
        </div>
      </div>

      {found && <div className="card" style={{ marginTop: 12 }}>{found.body}</div>}

      <h2>Latest blocks</h2>
      {err && <p className="err">chain unreachable: {err}</p>}
      <div className="card">
        <table>
          <thead><tr><th>#</th><th>Age</th><th>Txs</th><th>Gas used</th><th>Hash</th></tr></thead>
          <tbody>
            {blocks.map((b) => (
              <tr key={b.number}>
                <td><strong>{b.number}</strong></td>
                <td className="dim">{ago(b.timestamp)}</td>
                <td>{b.txCount === 0 ? <span className="dim">empty</span> : b.txCount}</td>
                <td className="dim">{b.gasUsed.toString()}</td>
                <td className="mono dim" title={b.hash}>{shortHex(b.hash, 8, 6)}</td>
              </tr>
            ))}
            {blocks.length === 0 && !err && (
              <tr><td colSpan={5} className="dim">loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="dim" style={{ fontSize: 13 }}>
        Mostly empty blocks and long gaps are correct here: the chain idles at one block a
        day and wakes within two seconds of a transaction.
      </p>
    </>
  );
}

function BlockCard({ b }: { b: ethers.Block }) {
  return (
    <table><tbody>
      <tr><th>Block</th><td><strong>{b.number}</strong></td></tr>
      <tr><th>Hash</th><td className="mono">{b.hash}</td></tr>
      <tr><th>Timestamp</th><td>{new Date(b.timestamp * 1000).toLocaleString()} <span className="dim">({ago(b.timestamp)})</span></td></tr>
      <tr><th>Transactions</th><td>{b.transactions.length}</td></tr>
      <tr><th>Gas used</th><td>{b.gasUsed.toString()} / {b.gasLimit.toString()}</td></tr>
      <tr><th>Parent</th><td className="mono">{b.parentHash}</td></tr>
    </tbody></table>
  );
}
